<template>
  <BittsContainer>
    <h2 :class="!allStoresReady || loading ? '' : 'mb-24'">Billing Summary</h2>
    <BillingErrorGuard :errored :loading="!allStoresReady || loading">
      <div>
        <div v-if="costPerSeat" class="cost-summary">
          <section
            class="cost-summary__section"
            data-testid="full-access"
            v-if="addedSeats"
          >
            <div>
              <span>{{ addedSeats }} x Connector Full Access Seats</span>
              <p
                >{{ centsToDollars(costPerSeat) }} per seat/{{
                  billingPeriod
                }}</p
              >
            </div>
            <p>{{ centsToDollars(totalNewSeatCost) }}</p>
          </section>
          <section
            class="cost-summary__section"
            data-testid="prorated-adjustment"
            v-if="proratedFee"
          >
            <div class="flex-row gap-4">
              <span>Prorated Adjustment </span>
              <BittsTooltip>
                <FontAwesomeIcon
                  :icon="['fak', 'information-circle']"
                  class="text-neutral-accent text-12 pb-1"
                />
                <template #title>
                  We only charge you for the time remaining in your subscription
                </template>
              </BittsTooltip></div
            >
            <p>-{{ centsToDollars(proratedFee) }}</p>
          </section>
          <section
            class="cost-summary__section font-bold text-m"
            data-testid="amount-due"
          >
            <div>Amount Due Today</div>
            <p class="text-success-text">{{
              centsToDollars(amountDueToday)
            }}</p>
          </section>
          <CurrentPlanCollapse
            :billing-period
            :core-seat-base
            :total-seat-cost="centsToDollars(currentSeatsTotalCost)"
            :has-v5-subscription="hasV5Subscription"
            :platform-fee="centsToDollars(currentPlatformFee)"
            :seat-cost="centsToDollars(currentCostPerSeat)"
          />
          <div class="cost-summary__next-invoice">
            <span
              v-html="
                injectStrings(V5_BILLING_COPY.seatPurchase.alert, {
                  values: injections,
                  bold: true,
                  italic: false,
                  underline: false,
                })
              "
            />
          </div>
          <div class="flex flex-col gap-16">
            <BittsButton
              :disabled="errored"
              class="w-full"
              text="Add seats"
              @click="emit('add-seats')"
            />
            <BittsButton
              class="w-full"
              text="Cancel"
              variant="ghost"
              @click="emit('cancel')"
            />
          </div>
        </div>
        <BittsEmptyState
          v-else
          data-testid="seat-cost-preview-empty-state"
          :font-awesome-icon="['fak', 'seat']"
          :title="V4_BILLING_COPY.connectorUpgrade.preview.emptyState.title"
          :description="
            V4_BILLING_COPY.connectorUpgrade.preview.emptyState.description
          "
          size="small"
        />
      </div>
    </BillingErrorGuard>
  </BittsContainer>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsContainer,
  BittsEmptyState,
  BittsTooltip,
} from '@crossbeam/bitts';

import { computed, onMounted, ref, watch } from 'vue';

import BillingErrorGuard from '@/components/billing/BillingErrorGuard.vue';
import CurrentPlanCollapse from '@/components/billing/CurrentPlanCollapse.vue';

import useBilling, { CostData } from '@/composables/useBilling';
import {
  BILLING_PLAN_ANNUAL,
  BILLING_PLAN_MONTHLY,
  V4_BILLING_COPY,
  V5_BILLING_COPY,
} from '@/constants/billing';
import { captureException } from '@/errors';
import { useBillingStore, useFlashesStore } from '@/stores';
import { centsToDollars, injectStrings } from '@/utils';

const { addedSeats, billingPeriod, coreSeatBase, subscriptionNextBillingDate } =
  defineProps<{
    addedSeats: number;
    billingPeriod: string;
    coreSeatBase: number;
    subscriptionNextBillingDate: string;
  }>();

const emit = defineEmits<{
  (e: 'add-seats' | 'cancel'): void;
  (e: 'amount-due-today', amountDueToday: number): void;
}>();

const loading = ref(true);
const errored = ref(false);
const billingStore = useBillingStore();
const flashesStore = useFlashesStore();
const { allStoresReady, previewConnector, processCostDataV4, fetchSeatDataV4 } =
  useBilling();

const isMonthly = computed(() => billingPeriod === 'month');
const chosenPeriod = computed(() =>
  isMonthly.value ? BILLING_PLAN_MONTHLY : BILLING_PLAN_ANNUAL,
);

const hasLegacySubscription = computed(() => billingStore.onLegacySubscription);
const hasV5Subscription = computed(() => billingStore.onV5Subscription);

// preview
const nextInvoice = ref(0);
const amountDueToday = ref(0);
const proratedFee = ref(0);
const costPerSeat = ref(0);
const coreSeatsTotalCost = ref(0);
const injections = computed(() => [
  centsToDollars(nextInvoice.value),
  subscriptionNextBillingDate,
]);

// current plan
const currentCostPerSeat = ref(0);
const currentSeatsTotalCost = ref(0);
const currentPlatformFee = ref(0);

const totalNewSeatCost = computed(() => addedSeats * costPerSeat.value);

onMounted(() => {
  previewCost();
});
watch(
  () => addedSeats,
  () => {
    previewCost();
  },
);
async function previewCost() {
  try {
    errored.value = false;
    loading.value = true;

    const costData = await fetchSeatDataV4({
      newCoreSeats: addedSeats,
      newSalesSeats: 0,
      period: chosenPeriod.value,
    });

    if (costData) {
      const processedData = processCostDataV4({
        costData,
        newCoreSeats: addedSeats,
        newSalesSeats: 0,
        period: chosenPeriod.value,
      });

      nextInvoice.value = processedData.nextSubscriptionCost as number;
      proratedFee.value = processedData.proratedFee as number;
      amountDueToday.value = processedData.amountDueToday as number;
      emit('amount-due-today', amountDueToday.value);
      costPerSeat.value = processedData.costPerCoreSeat as number;
      coreSeatsTotalCost.value = processedData.coreSeatsTotalCost;

      if (!currentCostPerSeat.value || !currentSeatsTotalCost.value) {
        const currentCostData = await previewConnector({
          seats: coreSeatBase,
          salesSeats: 0,
          period: chosenPeriod.value,
          hasLegacySubscription: hasLegacySubscription.value,
        });

        if (currentCostData) {
          const currentProcessedData = processCostDataV4({
            costData: currentCostData as unknown as Partial<CostData>,
            newCoreSeats: coreSeatBase,
            newSalesSeats: 0,
            period: chosenPeriod.value,
          });

          currentCostPerSeat.value =
            currentProcessedData.costPerCoreSeat as number;
          currentPlatformFee.value = currentProcessedData.platformFee as number;
          currentSeatsTotalCost.value = currentProcessedData.coreSeatsTotalCost;
        }
      }
    } else {
      nextInvoice.value = 0;
      proratedFee.value = 0;
      amountDueToday.value = 0;
      costPerSeat.value = 0;
      coreSeatsTotalCost.value = 0;
      currentCostPerSeat.value = 0;
      currentSeatsTotalCost.value = 0;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    errored.value = true;
    captureException(err);
    flashesStore.addErrorFlash({
      message: 'Could not preview cost',
      description: err.message,
    });
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
h2 {
  @apply font-bold text-neutral-text-strong text-lg;
}
.bitts-container {
  @apply rounded-bts-lg border-none p-24 bg-neutral-background-weak;
}
.cost-summary {
  @apply flex flex-col gap-24;
}
.cost-summary__next-invoice {
  @apply text-info-text bg-info-background-weak rounded-8 px-16 py-8 text-center;
}
p {
  @apply text-neutral-text-weak;
}
.cost-summary__section {
  @apply flex justify-between items-start border-b border-solid border-neutral-border pb-16;
  div {
    @apply flex flex-col text-neutral-text-strong;
  }
  &:last-of-type {
    @apply border-none pb-0;
  }
}
</style>
